import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Users,
  Activity,
  Mail,
  MessageSquare,
  Clock,
  TrendingUp,
  Fingerprint,
  Zap,
  Check,
  DollarSign,
  CreditCard,
  Coins,
} from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type {
  VerdictAdminStatsResponse,
  VerdictActiveLogicTreesResponse,
  VerdictFingerprintsResponse,
  VerdictResearchSessionsResponse,
  VerdictCostTrackingResponse,
} from '@/types';

// Helper function to format relative time
function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// Get default date range (last 7 days)
function getDefaultDateRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

// Summary stat card component
interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  trend?: string;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}

function StatCard({ title, value, icon, trend, color = 'primary' }: StatCardProps) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    error: 'bg-error/10 text-error',
    info: 'bg-info/10 text-info',
  };
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-text-muted">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {trend && <p className="text-xs text-text-muted mt-1">{trend}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
      </div>
    </Card>
  );
}

// Collapsible section component
interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
}

function CollapsibleSection({
  title, icon, count, defaultOpen = true, children, onSearch, searchPlaceholder = 'Search...',
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    onSearch?.(e.target.value);
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div
        className="p-4 border-b border-border flex items-center justify-between cursor-pointer hover:bg-surface-light/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="font-semibold">{title}</h2>
          {count !== undefined && <Badge variant="default" className="ml-2">{count}</Badge>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </div>
      {isOpen && (
        <div>
          {onSearch && (
            <div className="p-3 border-b border-border bg-surface-light/30">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full pl-9 pr-3 py-2 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          <div>{children}</div>
        </div>
      )}
    </Card>
  );
}


// Active logic trees section content
interface ActiveLogicTreesSectionProps {
  data: VerdictActiveLogicTreesResponse | null;
  searchQuery: string;
}

function ActiveLogicTreesContent({ data, searchQuery }: ActiveLogicTreesSectionProps) {
  if (!data) return <div className="p-4 text-center text-text-muted">Loading...</div>;

  const filterItem = (item: { title?: string; last_topic?: string | null; topic?: string; customer_email?: string; email?: string }) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return item.title?.toLowerCase().includes(q) || item.last_topic?.toLowerCase().includes(q) ||
      item.topic?.toLowerCase().includes(q) || item.customer_email?.toLowerCase().includes(q) || item.email?.toLowerCase().includes(q);
  };

  const sessions = data.active_sessions.filter(filterItem);
  const fingerprints = data.active_fingerprints.filter(filterItem);
  const reports = data.generating_reports.filter(filterItem);

  if (sessions.length === 0 && fingerprints.length === 0 && reports.length === 0) {
    return (
      <div className="p-8 text-center text-text-muted">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>{searchQuery ? 'No matching active sessions' : 'No active logic trees'}</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border max-h-96 overflow-y-auto">
      {sessions.map((s) => (
        <div key={s.session_id} className="p-4 hover:bg-surface-light/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{s.title || 'Untitled'}</span>
                <Badge variant="primary" className="text-xs">Dashboard</Badge>
              </div>
              <p className="text-sm text-text-secondary mt-1">{s.customer_email}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span><MessageSquare className="w-3 h-3 inline mr-1" />{s.message_count} msgs</span>
                <span><Clock className="w-3 h-3 inline mr-1" />{formatRelativeTime(s.updated_at)}</span>
              </div>
            </div>
            <Badge variant="success" className="text-xs"><Zap className="w-3 h-3 mr-1 animate-pulse" />Active</Badge>
          </div>
        </div>
      ))}
      {fingerprints.map((fp) => (
        <div key={fp.fingerprint_id} className="p-4 hover:bg-surface-light/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm">{fp.fingerprint_id}</span>
                <Badge variant="warning" className="text-xs">Landing Page</Badge>
                {fp.converted_to_lead && <Badge variant="success" className="text-xs">Converted</Badge>}
              </div>
              <p className="text-sm text-text-secondary mt-1 truncate">Topic: {fp.last_topic || 'N/A'}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span>{fp.query_count} queries</span>
                <span><Clock className="w-3 h-3 inline mr-1" />{formatRelativeTime(fp.last_query_at)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
      {reports.map((r) => (
        <div key={r.report_id} className="p-4 hover:bg-surface-light/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium truncate">{r.topic}</span>
                <Badge variant="primary" className="text-xs">{r.tier_used}</Badge>
              </div>
              <p className="text-sm text-text-secondary mt-1">{r.email}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span><Clock className="w-3 h-3 inline mr-1" />{formatRelativeTime(r.created_at)}</span>
              </div>
            </div>
            <Badge variant="warning" className="text-xs"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Generating</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}


// Fingerprints list content
function FingerprintsContent({ data, searchQuery }: { data: VerdictFingerprintsResponse | null; searchQuery: string }) {
  if (!data) return <div className="p-4 text-center text-text-muted">Loading...</div>;

  const filtered = data.fingerprints.filter(fp => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return fp.fingerprint_id.toLowerCase().includes(q) || fp.first_topic?.toLowerCase().includes(q) || fp.last_topic?.toLowerCase().includes(q);
  });

  if (filtered.length === 0) {
    return <div className="p-8 text-center text-text-muted"><Fingerprint className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No fingerprints found</p></div>;
  }

  return (
    <div className="divide-y divide-border max-h-96 overflow-y-auto">
      {filtered.map((fp, index) => (
        <div key={`${fp.fingerprint_id}-${index}`} className="p-4 hover:bg-surface-light/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm">{fp.fingerprint_id}</span>
                <Badge variant="default" className="text-xs">{fp.query_count} queries</Badge>
                {fp.converted_to_lead && <Badge variant="success" className="text-xs">Converted</Badge>}
              </div>
              <p className="text-sm text-text-secondary mt-1 truncate">Last: {fp.last_topic || 'N/A'}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span>First: {formatRelativeTime(fp.first_query_at)}</span>
                <span>Last: {formatRelativeTime(fp.last_query_at)}</span>
                {fp.ip_address && <span>IP: {fp.ip_address}</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Research sessions list content
function SessionsContent({ data, searchQuery }: { data: VerdictResearchSessionsResponse | null; searchQuery: string }) {
  if (!data) return <div className="p-4 text-center text-text-muted">Loading...</div>;

  const filtered = data.sessions.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return s.title?.toLowerCase().includes(q) || s.customer_email.toLowerCase().includes(q) || s.customer_name.toLowerCase().includes(q);
  });

  if (filtered.length === 0) {
    return <div className="p-8 text-center text-text-muted"><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No sessions found</p></div>;
  }

  return (
    <div className="divide-y divide-border max-h-96 overflow-y-auto">
      {filtered.map((s) => (
        <div key={s.session_id} className="p-4 hover:bg-surface-light/30">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{s.title || 'Untitled Session'}</span>
                <Badge variant={s.status === 'active' ? 'success' : 'default'} className="text-xs">{s.status}</Badge>
                <Badge variant="default" className="text-xs">{s.message_count} msgs</Badge>
              </div>
              <p className="text-sm text-text-secondary mt-1">{s.customer_name} ({s.customer_email})</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-text-muted">
                <span>Created: {formatRelativeTime(s.created_at)}</span>
                <span>Updated: {formatRelativeTime(s.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Cost tracking content component
function CostTrackingContent({ data, searchQuery }: { data: VerdictCostTrackingResponse | null; searchQuery: string }) {
  if (!data) return <div className="p-4 text-center text-text-muted">Loading...</div>;

  const filtered = data.users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q) || u.user_tier.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6">
      {/* Cost Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-surface-light/20 rounded-lg">
        <div className="text-center">
          <p className="text-xs text-text-muted uppercase">Platform Cost</p>
          <p className="text-xl font-bold text-success">${data.summary.total_platform_cost.toFixed(2)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted uppercase">Free Tier Est.</p>
          <p className="text-xl font-bold text-warning">${data.summary.free_tier_estimated_cost.toFixed(2)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted uppercase">Total Est. Cost</p>
          <p className="text-xl font-bold text-primary">${data.summary.total_estimated_cost.toFixed(2)}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-text-muted uppercase">Total Tokens</p>
          <p className="text-xl font-bold text-info">{data.summary.total_platform_tokens.toLocaleString()}</p>
        </div>
      </div>

      {/* Free Tier Stats */}
      <div className="px-4">
        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-primary" />
          Free Tier Breakdown
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="bg-surface border border-border rounded-lg p-3">
            <p className="text-text-muted">Basic Reports</p>
            <p className="font-medium">{data.free_tier_stats.basic_tier_queries} <span className="text-xs text-text-muted">($0.02/ea)</span></p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3">
            <p className="text-text-muted">Standard Reports</p>
            <p className="font-medium">{data.free_tier_stats.standard_tier_queries} <span className="text-xs text-text-muted">($0.05/ea)</span></p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3">
            <p className="text-text-muted">Quick Chats</p>
            <p className="font-medium">{data.free_tier_stats.fingerprint_queries} <span className="text-xs text-text-muted">($0.005/ea)</span></p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3">
            <p className="text-text-muted">Est. Cost</p>
            <p className="font-medium text-warning">${data.free_tier_stats.estimated_cost.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Credit Transactions Summary */}
      {Object.keys(data.credit_transactions).length > 0 && (
        <div className="px-4">
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-success" />
            Credit Transactions
          </h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.credit_transactions).map(([type, tx]) => (
              <Badge key={type} variant="default" className="text-xs">
                {type.replace(/_/g, ' ')}: {tx.count} txns ({tx.total_credits.toLocaleString()} credits)
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="px-4">
        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
          <Users className="w-4 h-4 text-info" />
          User Cost Breakdown ({filtered.length} users)
        </h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-light/50">
            <tr className="text-left text-text-muted">
              <th className="px-4 py-2 font-medium">User</th>
              <th className="px-4 py-2 font-medium">Tier</th>
              <th className="px-4 py-2 font-medium text-right">Tokens</th>
              <th className="px-4 py-2 font-medium text-right">Cost</th>
              <th className="px-4 py-2 font-medium text-right">Credits</th>
              <th className="px-4 py-2 font-medium text-right">Usage %</th>
              <th className="px-4 py-2 font-medium text-right">API Calls</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.slice(0, 50).map((user) => (
              <tr key={user.customer_id} className="hover:bg-surface-light/30">
                <td className="px-4 py-2">
                  <div className="font-medium truncate max-w-[200px]">{user.name}</div>
                  <div className="text-xs text-text-muted truncate">{user.email}</div>
                </td>
                <td className="px-4 py-2">
                  <Badge
                    variant={user.is_master_user ? 'success' : user.user_tier === 'trial' ? 'warning' : 'default'}
                    className="text-xs"
                  >
                    {user.is_master_user ? 'Master' : user.user_tier}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {user.total_tokens.toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs text-success">
                  ${user.total_cost.toFixed(4)}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {user.is_master_user ? (
                    <span className="text-text-muted">Unlimited</span>
                  ) : (
                    <span>{(user.credit_balance ?? 0).toLocaleString()}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {user.is_master_user ? (
                    <span className="text-text-muted">-</span>
                  ) : user.credit_usage_percentage !== null ? (
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-2 bg-surface-light rounded-full overflow-hidden">
                        <div
                          className={`h-full ${user.credit_usage_percentage > 80 ? 'bg-error' : user.credit_usage_percentage > 50 ? 'bg-warning' : 'bg-success'}`}
                          style={{ width: `${Math.min(user.credit_usage_percentage, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono">{user.credit_usage_percentage.toFixed(1)}%</span>
                    </div>
                  ) : (
                    <span className="text-text-muted">0%</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">
                  {user.total_api_calls}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 50 && (
          <p className="px-4 py-2 text-xs text-text-muted">Showing 50 of {filtered.length} users</p>
        )}
      </div>

      {/* Daily Costs */}
      {data.daily_costs.length > 0 && (
        <div className="px-4">
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Coins className="w-4 h-4 text-warning" />
            Daily Credit Usage (Last 7 Days)
          </h4>
          <div className="grid grid-cols-7 gap-2">
            {data.daily_costs.slice(0, 7).map((day) => (
              <div key={day.date} className="bg-surface border border-border rounded-lg p-2 text-center">
                <p className="text-xs text-text-muted">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}</p>
                <p className="font-mono text-sm">{day.credits_used.toLocaleString()}</p>
                <p className="text-xs text-success">${day.estimated_cost.toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main content component with all state and data fetching
function VerdictAdminMonitoringContent() {
  const [stats, setStats] = useState<VerdictAdminStatsResponse | null>(null);
  const [activeLogicTrees, setActiveLogicTrees] = useState<VerdictActiveLogicTreesResponse | null>(null);
  const [fingerprints, setFingerprints] = useState<VerdictFingerprintsResponse | null>(null);
  const [sessions, setSessions] = useState<VerdictResearchSessionsResponse | null>(null);
  const [costTracking, setCostTracking] = useState<VerdictCostTrackingResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const defaultDates = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  // Search queries for each section
  const [activeTreesSearch, setActiveTreesSearch] = useState('');
  const [fingerprintsSearch, setFingerprintsSearch] = useState('');
  const [sessionsSearch, setSessionsSearch] = useState('');
  const [costTrackingSearch, setCostTrackingSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, treesRes, fingerprintsRes, sessionsRes, costRes] = await Promise.all([
        api.getVerdictAdminStats({ start_date: startDate || undefined, end_date: endDate || undefined }),
        api.getVerdictActiveLogicTrees(),
        api.getVerdictFingerprints({ limit: 100 }),
        api.getVerdictResearchSessions({ limit: 100 }),
        api.getVerdictCostTracking({ start_date: startDate || undefined, end_date: endDate || undefined }),
      ]);
      setStats(statsRes);
      setActiveLogicTrees(treesRes);
      setFingerprints(fingerprintsRes);
      setSessions(sessionsRes);
      setCostTracking(costRes);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch verdict admin data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
    if (autoRefresh) {
      const interval = window.setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh]);

  const handleRefresh = () => {
    setIsLoading(true);
    fetchData();
  };

  const totalActive = activeLogicTrees
    ? activeLogicTrees.active_sessions.length + activeLogicTrees.active_fingerprints.length + activeLogicTrees.generating_reports.length
    : 0;

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Verdict Admin Monitoring</h1>
          <p className="text-text-muted text-sm mt-1">Monitor Verdict platform activity and logic trees</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Picker */}
          <div className="flex items-center gap-2 bg-surface border border-border rounded-lg px-3 py-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
            />
            <span className="text-text-muted">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent text-sm focus:outline-none"
            />
          </div>
          {/* Auto-refresh toggle */}
          <Button
            variant={autoRefresh ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? <Check className="w-4 h-4 mr-1" /> : null}
            Auto-refresh
          </Button>
          {/* Manual refresh */}
          <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Last refresh indicator */}
      <p className="text-xs text-text-muted">Last updated: {lastRefresh.toLocaleTimeString()}</p>

      {/* Summary Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard
            title="Total Fingerprints"
            value={stats.fingerprint_stats.total_fingerprints}
            icon={<Fingerprint className="w-5 h-5" />}
            color="primary"
          />
          <StatCard
            title="Multi-Query Users"
            value={stats.fingerprint_stats.multi_query_users}
            icon={<Users className="w-5 h-5" />}
            color="info"
          />
          <StatCard
            title="Total Queries"
            value={stats.fingerprint_stats.total_queries}
            icon={<Activity className="w-5 h-5" />}
            color="warning"
          />
          <StatCard
            title="Total Leads"
            value={stats.lead_stats.total_leads}
            icon={<Mail className="w-5 h-5" />}
            color="success"
          />
          <StatCard
            title="Dashboard Sessions"
            value={stats.session_stats.total_sessions}
            icon={<MessageSquare className="w-5 h-5" />}
            color="primary"
          />
          <StatCard
            title="Active Now"
            value={totalActive}
            icon={<Zap className="w-5 h-5" />}
            trend={totalActive > 0 ? 'Logic trees running' : 'No active sessions'}
            color={totalActive > 0 ? 'success' : 'info'}
          />
        </div>
      )}

      {/* Conversion Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Converted to Lead"
            value={stats.fingerprint_stats.converted_to_lead}
            icon={<TrendingUp className="w-5 h-5" />}
            trend={`${((stats.fingerprint_stats.converted_to_lead / Math.max(stats.fingerprint_stats.total_fingerprints, 1)) * 100).toFixed(1)}% conversion`}
            color="success"
          />
          <StatCard
            title="Total Reports"
            value={stats.lead_stats.total_reports}
            icon={<Check className="w-5 h-5" />}
            color="success"
          />
          <StatCard
            title="Reports Sent"
            value={stats.lead_stats.reports_sent}
            icon={<Activity className="w-5 h-5" />}
            color="info"
          />
          <StatCard
            title="Reports Generating"
            value={stats.lead_stats.reports_generating}
            icon={<TrendingUp className="w-5 h-5" />}
            color="primary"
          />
        </div>
      )}


      {/* Active Logic Trees Section */}
      <CollapsibleSection
        title="Active Logic Trees"
        icon={<Zap className="w-5 h-5 text-warning" />}
        count={totalActive}
        onSearch={setActiveTreesSearch}
        searchPlaceholder="Search by topic, email..."
      >
        <ActiveLogicTreesContent data={activeLogicTrees} searchQuery={activeTreesSearch} />
      </CollapsibleSection>

      {/* Fingerprints List Section */}
      <CollapsibleSection
        title="Landing Page Fingerprints"
        icon={<Fingerprint className="w-5 h-5 text-primary" />}
        count={fingerprints?.total}
        defaultOpen={false}
        onSearch={setFingerprintsSearch}
        searchPlaceholder="Search by fingerprint ID, topic..."
      >
        <FingerprintsContent data={fingerprints} searchQuery={fingerprintsSearch} />
      </CollapsibleSection>

      {/* Research Sessions Section */}
      <CollapsibleSection
        title="Dashboard Research Sessions"
        icon={<MessageSquare className="w-5 h-5 text-info" />}
        count={sessions?.total}
        defaultOpen={false}
        onSearch={setSessionsSearch}
        searchPlaceholder="Search by title, user..."
      >
        <SessionsContent data={sessions} searchQuery={sessionsSearch} />
      </CollapsibleSection>

      {/* Cost Tracking Section */}
      <CollapsibleSection
        title="Cost Tracking & Credits"
        icon={<DollarSign className="w-5 h-5 text-success" />}
        count={costTracking?.summary.active_paid_users}
        defaultOpen={false}
        onSearch={setCostTrackingSearch}
        searchPlaceholder="Search by email, name, tier..."
      >
        <CostTrackingContent data={costTracking} searchQuery={costTrackingSearch} />
      </CollapsibleSection>
    </div>
  );
}

// Main exported page component with admin access check
export function VerdictAdminMonitoringPage() {
  const { user, isLoading: authLoading } = useAuth();

  // Redirect non-admin users
  if (!authLoading && (!user || !user.is_master_user)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return <VerdictAdminMonitoringContent />;
}